import { DeleteOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tooltip } from 'antd';
import { type FormItemProps } from 'antd/es/form';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import ResponsiveStyledTable from '../../pages/shared/ResponsiveStyledTable';
import FormFields from '../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../utilities/extensions';
import FormItem from './FormItem';
import SingleUploadField from './SingleUploadField';

export type SocialMediaUrlFieldArrayProps = {
    name: string;
    label?: string;
    disabled?: boolean;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
    addSocialMedia: () => void;
};

const SocialMediaUrlFieldArray = ({
    name,
    label,
    disabled,
    labelForAdd,
    itemProps,
    limit,
    addSocialMedia,
    ...props
}: SocialMediaUrlFieldArrayProps) => {
    const { t } = useTranslation('common');

    const [field] = useField({ name });
    const { value } = field;

    return (
        <FormItem {...itemProps} label={label} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <ResponsiveStyledTable
                            dataSource={value}
                            pagination={false}
                            rowKey={(row, index: number) => `${name}_${index.toString()}`}
                        >
                            <Table.Column
                                dataIndex="altText"
                                render={(value, record, index) => (
                                    <FormFields.InputField disabled={disabled} name={`${name}.[${index}].altText`} />
                                )}
                                title={t('common:fields.socialMedia.imageAltText.label')}
                            />
                            <Table.Column
                                dataIndex="icon"
                                render={(value, record, index) => (
                                    <SingleUploadField
                                        disabled={disabled}
                                        errorMessage={t('common:fields.socialMedia.icon.fileError')}
                                        extensions={allowedExtensions.image}
                                        name={`${name}.[${index}].icon`}
                                    />
                                )}
                                title={
                                    <span>
                                        {t('common:fields.socialMedia.icon.label')}{' '}
                                        <Tooltip title={t('common:fields.socialMedia.icon.tooltip')}>
                                            <InfoCircleOutlined
                                                style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: '4px' }}
                                            />
                                        </Tooltip>
                                    </span>
                                }
                            />
                            <Table.Column
                                dataIndex="url"
                                render={(value, record, index) => (
                                    <FormFields.InputField
                                        addonBefore="https://"
                                        disabled={disabled}
                                        name={`${name}.[${index}].url`}
                                    />
                                )}
                                title={t('common:fields.socialMedia.url.label')}
                            />
                            <Table.Column
                                key="actions"
                                align="center"
                                render={(_, __, index) =>
                                    !disabled && (
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => {
                                                arrayHelpers.remove(index);
                                            }}
                                            size="middle"
                                            type="link"
                                        />
                                    )
                                }
                                title={t('common:actions.action')}
                                width={100}
                            />
                        </ResponsiveStyledTable>
                        {!disabled && (
                            <Button icon={<PlusCircleOutlined />} onClick={addSocialMedia} type="link">
                                {t('companyDetails:actions.addSocialMediaIcons')}
                            </Button>
                        )}
                    </Space>
                )}
            />
        </FormItem>
    );
};

export default SocialMediaUrlFieldArray;
